import React, { useEffect, useState } from 'react';
import {
 Button,
 Flex,
 FormControl,
 FormErrorMessage,
 FormLabel,
 Input,
 Modal,
 ModalBody,
 ModalCloseButton,
 ModalContent,
 ModalFooter,
 ModalHeader,
 ModalOverlay,
 Select,
 Stack,
 Textarea,
 useToast,
} from '@chakra-ui/react';
import baseService from '../../services/base';
import { useGriSeries } from '../../hooks/useGriSeries';

const NOVA_SERIE = '__nova_serie__';

const EMPTY = {
 code: '',
 name: '',
 description: '',
 series: '',
 unit: '',
 weight: '',
};

/**
 * Criação e edição de indicador do catálogo GRI. O mesmo formulário serve aos
 * dois casos — o que muda é o método e a rota.
 */
export default function GriIndicatorModal({ isOpen, onClose, indicator, onSaved }) {
 const toast = useToast();
 const [form, setForm] = useState(EMPTY);
 const [errors, setErrors] = useState({});
 const [isSaving, setIsSaving] = useState(false);
 // GRI-09: criar série sem sair do cadastro de indicador — antes, uma série
 // nova exigia migration; agora é só um nome aqui.
 const [novaSerieNome, setNovaSerieNome] = useState('');
 const [isSavingSerie, setIsSavingSerie] = useState(false);
 const { options: seriesOptions, mutate: mutateSeries } = useGriSeries();

 const isEditing = !!indicator?.id_gri_indicator;

 useEffect(() => {
  if (!isOpen) return;
  setErrors({});
  setNovaSerieNome('');
  setForm(
   indicator
    ? {
       code: indicator.code || '',
       name: indicator.name || '',
       description: indicator.description || '',
       series: indicator.series || '',
       unit: indicator.unit || '',
       weight: indicator.weight ?? '',
      }
    : EMPTY
  );
 }, [isOpen, indicator]);

 function handleSeriesChange(value) {
  if (value === NOVA_SERIE) {
   setNovaSerieNome('');
   setField('series', NOVA_SERIE);
   return;
  }
  setField('series', value);
 }

 async function criarSerie() {
  const label = novaSerieNome.trim();
  if (!label) return;

  setIsSavingSerie(true);
  try {
   const response = await baseService.post('gri-series', { label });
   await mutateSeries();
   setField('series', response.data.key);
   setNovaSerieNome('');
   toast({ title: 'Série criada', status: 'success' });
  } catch (error) {
   const message = error?.response?.data?.message;
   toast({
    title: 'Não foi possível criar a série',
    description: Array.isArray(message) ? message[0] : message,
    status: 'error',
   });
  } finally {
   setIsSavingSerie(false);
  }
 }

 function setField(field, value) {
  setForm((prev) => ({ ...prev, [field]: value }));
  setErrors((prev) => ({ ...prev, [field]: undefined }));
 }

 function validate() {
  const next = {};
  if (!form.code.trim()) next.code = 'Informe o código GRI';
  if (!form.name.trim()) next.name = 'Informe o nome';
  if (!form.series || form.series === NOVA_SERIE) {
   next.series = 'Selecione ou crie a série';
  }
  if (!form.unit.trim()) next.unit = 'Informe a unidade de medida';
  if (form.weight !== '' && Number.isNaN(Number(form.weight))) {
   next.weight = 'Use um número';
  }
  setErrors(next);
  return Object.keys(next).length === 0;
 }

 async function handleSubmit() {
  if (!validate()) return;

  const payload = {
   code: form.code.trim(),
   name: form.name.trim(),
   description: form.description.trim() || undefined,
   series: form.series,
   unit: form.unit.trim(),
   weight: form.weight === '' ? undefined : Number(form.weight),
  };

  setIsSaving(true);
  try {
   if (isEditing) {
    await baseService.patch(`gri-indicator/${indicator.id_gri_indicator}`, payload);
   } else {
    await baseService.post('gri-indicator', payload);
   }
   toast({
    title: isEditing ? 'Indicador atualizado' : 'Indicador cadastrado',
    status: 'success',
   });
   await onSaved();
   onClose();
  } catch (error) {
   // Código duplicado volta como 409 com a mensagem de negócio do backend.
   const message = error?.response?.data?.message;
   if (error?.response?.status === 409) {
    setErrors((prev) => ({ ...prev, code: message }));
   } else {
    toast({
     title: 'Não foi possível salvar o indicador',
     description: Array.isArray(message) ? message[0] : message,
     status: 'error',
    });
   }
  } finally {
   setIsSaving(false);
  }
 }

 return (
  <Modal isOpen={isOpen} onClose={onClose} isCentered size='xl'>
   <ModalOverlay />
   <ModalContent>
    <ModalHeader>{isEditing ? 'Editar indicador GRI' : 'Novo indicador GRI'}</ModalHeader>
    <ModalCloseButton />

    <ModalBody>
     <Stack spacing='16px'>
      <FormControl isInvalid={!!errors.code} isRequired>
       <FormLabel fontSize='sm'>Código GRI</FormLabel>
       <Input
        placeholder='GRI 305-1'
        value={form.code}
        onChange={(e) => setField('code', e.target.value)}
       />
       <FormErrorMessage>{errors.code}</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={!!errors.name} isRequired>
       <FormLabel fontSize='sm'>Nome</FormLabel>
       <Input
        placeholder='Emissões diretas de gases de efeito estufa — Escopo 1'
        value={form.name}
        onChange={(e) => setField('name', e.target.value)}
       />
       <FormErrorMessage>{errors.name}</FormErrorMessage>
      </FormControl>

      <FormControl>
       <FormLabel fontSize='sm'>Descrição</FormLabel>
       <Textarea
        rows={3}
        placeholder='Detalhe o que o indicador mede (opcional)'
        value={form.description}
        onChange={(e) => setField('description', e.target.value)}
       />
      </FormControl>

      <FormControl isInvalid={!!errors.series} isRequired>
       <FormLabel fontSize='sm'>Série</FormLabel>
       {form.series === NOVA_SERIE ? (
        <Flex gap='8px'>
         <Input
          autoFocus
          placeholder='Nome da nova série (ex.: Governança)'
          value={novaSerieNome}
          onChange={(e) => setNovaSerieNome(e.target.value)}
          onKeyDown={(e) => {
           if (e.key === 'Enter') {
            e.preventDefault();
            criarSerie();
           }
          }}
         />
         <Button
          variant='primary'
          isLoading={isSavingSerie}
          isDisabled={!novaSerieNome.trim()}
          onClick={criarSerie}
          flexShrink={0}
         >
          Criar
         </Button>
         <Button variant='cancel' onClick={() => setField('series', '')} flexShrink={0}>
          Cancelar
         </Button>
        </Flex>
       ) : (
        <Select
         placeholder='Selecione a série'
         value={form.series}
         onChange={(e) => handleSeriesChange(e.target.value)}
        >
         {seriesOptions.map((option) => (
          <option key={option.value} value={option.value}>
           {option.label}
          </option>
         ))}
         <option value={NOVA_SERIE}>+ Criar nova série</option>
        </Select>
       )}
       <FormErrorMessage>{errors.series}</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={!!errors.unit} isRequired>
       <FormLabel fontSize='sm'>Unidade de medida</FormLabel>
       <Input
        placeholder='tCO2e, m³, R$, %, Número absoluto'
        value={form.unit}
        onChange={(e) => setField('unit', e.target.value)}
       />
       <FormErrorMessage>{errors.unit}</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={!!errors.weight}>
       <FormLabel fontSize='sm'>Nota/Peso</FormLabel>
       <Input
        type='number'
        step='0.01'
        min='0'
        placeholder='Opcional'
        value={form.weight}
        onChange={(e) => setField('weight', e.target.value)}
       />
       <FormErrorMessage>{errors.weight}</FormErrorMessage>
      </FormControl>
     </Stack>
    </ModalBody>

    <ModalFooter gap='12px'>
     <Button variant='cancel' onClick={onClose}>
      Cancelar
     </Button>
     <Button variant='primary' isLoading={isSaving} onClick={handleSubmit}>
      Salvar
     </Button>
    </ModalFooter>
   </ModalContent>
  </Modal>
 );
}
